import { Text, View, useWindowDimensions, Pressable } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import CardGrid from '@/components/General/CardGrid';
import BibleStudyCard from '@/components/BibleStudies/BibleStudyCard';
import BibleStudySessionsTable from '@/components/BibleStudies/BibleStudySessionsTable';
import BibleStudySetupNeeded from '@/components/BibleStudies/BibleStudySetupNeeded';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import usePlatform from '@/utils/hooks/usePlatform';
import { useEffect, useState, useMemo } from 'react';
import { useRouter, useNavigation } from 'expo-router';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { Table } from '@/components/General/Table';
import Avatar from '@/components/People/Avatar';
import { Tooltip } from '@/components/General/Tooltip';
import DropdownMenu from '@/components/General/DropdownMenu';
import TableFilters from '@/components/General/TableFilters';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import PageTitle from '@/components/General/PageTitle';
import useBibleStudies from '@/utils/hooks/useBibleStudies';
import useSaveShortcut from '@/utils/hooks/useSaveShortcut';

export default function BibleStudiesScreen(){
    /*** State ***/
    const [searchText, setSearchText] = useState("");
    const [tabIndex, setTabIndex] = useState(0);
    const [scope, setScope] = useState('own'); // 'own' | 'all' — 'all' only offered to managers
    const [sortConfig, setSortConfig] = useState(null);
    const [filters, setFilters] = useState([]);
    const [suggestedSearch, setSuggestedSearch] = useState('');

    const layout = useWindowDimensions();

    /*** Theme & Dimensions ***/
    const {theme: {colors, fonts}} = useUnistyles();
    const { isLandscape, isNative } = usePlatform();
    const router = useRouter();
    const navigation = useNavigation();

    /*** Bible Studies gate ***/
    const { enabled, loading, canView, canManage, canCreate, canViewReports, setupNeeded, bibleStudyType, roleID } = useBibleStudies();
    const effectiveScope = canManage ? scope : 'own';

    const tabRoutes = useMemo(() => ([
        { key: 'studies', title: 'Studies' },
        { key: 'sessions', title: 'Sessions' }
    ]), []);

    // Flag/permission guard — UI gating only; backend RPCs enforce regardless.
    useEffect(() => {
        if (!loading && (!enabled || !canView)) {
            router.canGoBack() ? router.back() : router.replace("/");
        }
    }, [loading, enabled, canView, router]);

    const {data: loggedInUser, isLoading: isLoadingLoggedInUser} = Query.people.loggedInUser()
    const saveShortcut = useSaveShortcut(loggedInUser?.id);

    const teacherFilter = filters.find(f => f.id === 'teacher')?.value;
    const statusFilter = filters.find(f => f.id === 'status')?.value;

    const {data: bibleStudies, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.bibleStudies.all({
        scope: effectiveScope,
        status: statusFilter || null,
        searchTerm: searchText,
        teacherID: teacherFilter?.id || null,
        sortKey: sortConfig?.key,
        sortDirection: sortConfig?.direction,
        options: {enabled: enabled && canView && !setupNeeded && !isLoadingLoggedInUser}
    });

    const suggestedTeachersQuery = Query.people.all({
        filters: roleID ? { conditions: [{ fieldId: 'roles', operator: 'contains_any', value: [roleID] }] } : undefined,
        filterLogic: 'AND',
        searchTerm: suggestedSearch,
        options: { enabled: roleID != null }
    });

    const queryClient = useQueryClient()

    /*** Filters ***/
    useEffect(() => {
        setFilters([
            {
                id: "teacher",
                inputType: "component",
                label: "Teacher",
                value: null
            },
            {
                id: "status",
                inputType: "dropdown",
                label: "Status",
                value: null,
                options: [
                    {label: "Active", value: "ACTIVE"},
                    {label: "Canceled", value: "CANCELED"},
                    {label: "Completed", value: "COMPLETED"},
                    {label: "Paused", value: "PAUSED"}
                ]
            },
        ]);
    }, []);

    const updateFilterValue = (filterId, value) => {
        setFilters(prev => prev.map(f => f.id === filterId ? {...f, value} : f));
    };

    const filtersWithComponents = filters.map(f => {
        if(f.id === "teacher"){
            return {...f, component: (
                <View style={styles.teacherFilterContainer}>
                    <SheetSelector
                        titleOnly
                        type="people"
                        queryResult={roleID ? suggestedTeachersQuery : undefined}
                        queryResultSearch={setSuggestedSearch}
                        title="Teacher"
                        value={f.value}
                        onChange={(selected) => updateFilterValue("teacher", selected)}
                    />
                </View>
            )};
        }
        return f;
    });

    useEffect(() => {
        navigation.setOptions({
            headerTitle: () => <PageTitle title="Bible Studies" />
        });
    }, [navigation])

    useEffect(() => {
        const options = []

        options.push({
            name: "Save to Shortcuts",
            icon: 'star-outline',
            onSelect: () => saveShortcut({ screen: `/biblestudies`, label: 'Bible Studies', icon: 'book-outline' })
        });

        if(canViewReports) options.push({name: "Reports", icon: 'bar-chart-outline', onSelect: () => router.push({pathname: "/biblestudies/reports"})})

        if(canCreate) options.push({name: "New Study", icon: 'add', onSelect: () => router.push({pathname: "/biblestudies/create"})})

        navigation.setOptions({
            headerRight: () => <DropdownMenu testID="HeaderMenu" options={options} />
        })
    }, [navigation, canCreate, canViewReports, saveShortcut])


    const flatStudies = useMemo(() => {
        if (!bibleStudies) return [];
        return bibleStudies.pages.flatMap(page => page.data).map(study => ({
            ...study,
            lastTaught: study.Event?.EventInstances?.[0]?.startTime
        }));
    }, [bibleStudies]);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'ACTIVE': return {text:"success", background:"successContainer"}
            case 'CANCELED': return {text:"error", background:"errorContainer"}
            case 'COMPLETED': return {text:"info", background:"onInfo"}
            case 'PAUSED': return {text:"warning", background:"warningContainer"}
            default: return {text:"text", background:"textSecondary"};
        }
    }

    const renderItem = ({item}) => {
        return <BibleStudyCard study={item} />
    }

    if (loading || (enabled && canView && !setupNeeded && isLoadingLoggedInUser)) {
        return <View style={styles.loadingContainer}><ActivityIndicator size="large" color={colors.primary} /></View>;
    }

    if (enabled && setupNeeded) {
        return <BibleStudySetupNeeded canManage={canManage} />;
    }

    const scopeToggle = canManage && (
        <View style={styles.scopeRow}>
            {[{key:'own', label:'My Studies'}, {key:'all', label:'All Studies'}].map(option => (
                <Pressable
                    key={option.key}
                    testID={`bible-studies-scope-${option.key}`}
                    accessibilityRole="button"
                    accessibilityState={{ selected: effectiveScope === option.key }}
                    onPress={() => setScope(option.key)}
                    style={styles.scopePill(effectiveScope === option.key)}
                >
                    <Text style={styles.scopePillText(effectiveScope === option.key)}>{option.label}</Text>
                </Pressable>
            ))}
        </View>
    );

    return (
        <View style={styles.container}>
            <TabView
                swipeEnabled={isNative}
                navigationState={{ index: tabIndex, routes: tabRoutes }}
                onIndexChange={setTabIndex}
                initialLayout={{ width: layout.width }}
                renderTabBar={(props) => <ReachTabBar {...props} />}
                lazy
                renderScene={({ route }) => {
                    switch (route.key) {
                        case 'sessions':
                            return (
                                <View style={styles.tableContainer}>
                                    {scopeToggle}
                                    <BibleStudySessionsTable
                                        scope={effectiveScope}
                                        smallGroupTypeID={bibleStudyType?.id}
                                        title="Sessions"
                                        description={effectiveScope === 'own' ? 'Sessions for studies you teach.' : 'All Bible Study sessions. Use the filters and search bar to find specific sessions.'}
                                        roleID={roleID}
                                    />
                                </View>
                            );
                        case 'studies':
                            return (
                                <View style={styles.listContainer}>
                {isNative && isLandscape
                    ? <View style={styles.tableContainer}>
                        {scopeToggle}
                        <Table
                        keyExtractor={(item) => item.id}
                        onRefresh={() => queryClient.invalidateQueries({queryKey: QueryKeys.bibleStudies.all()})}
                        title={effectiveScope === 'own' ? 'My Studies' : 'All Studies'}
                        description={effectiveScope === 'own' ? 'View and manage the Bible Studies you teach.' : 'View all Bible Studies in your organization.'}
                        filters={filtersWithComponents}
                        onFiltersChange={setFilters}
                        onClearFilters={() => setFilters(filters.map(f => ({...f, value: null})))}
                        onSearchChange={setSearchText}
                        isLoading={isLoading || isFetching}
                        onRowClick={(study) => router.push("/biblestudies/" + study.id)}
                        data={isLoading ? [] : flatStudies}
                        onEndReachedThreshold={0.1}
                        onEndReached={() => {
                            if(!isLoading && !isFetchingNextPage && hasNextPage) fetchNextPage()
                        }}
                        actions={[
                            {
                                name: "Log a Session",
                                icon: {web: 'add'},
                                isAvailable: (study) => study.status === 'ACTIVE' || study.status === 'PAUSED',
                                onSelect: (study) => {
                                    router.push({pathname: "/biblestudies/sessions/create", params: {smallGroupID: study.id}})
                                }
                            },
                            {
                                name: "Edit",
                                icon: {web: 'create-outline'},
                                onSelect: (study) => router.push({pathname: "/biblestudies/create", params: {id: study.id}})
                            },
                        ]}
                        sortConfig={sortConfig}
                        onSortChange={setSortConfig}
                        columns={[
                            {accessor: "name", value: "Name", sortable: true},
                            {accessor: "lastTaught", value: "Last Taught", sortable: true, renderer: (study) => study.lastTaught ? new Date(study.lastTaught).toLocaleDateString() : "Never"},
                            {accessor: "status", value: "Status", sortable: true, renderer: (study) => {
                                const color = getStatusStyle(study.status);
                                return <View style={styles.pill(color.background, color.text)}>
                                    <Text style={styles.pillText(color.text)}>{study.status}</Text>
                                </View>
                            }},
                            {accessor: "leader", value: "Teacher(s)", sortable: true, renderer: (study) => (
                                <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                                    <Avatar profile={study.Leader} size={30} disableBorder/>
                                    <Text style={{color:colors.text, fontSize:12, fontFamily:fonts.heading}}>{study.Leader?.firstName} {study.Leader?.lastName}</Text>
                                </View>
                            )},
                            {accessor: "coLeader", value: "", renderer: (study) => study.CoLeader ? (
                                <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                                    <Avatar profile={study.CoLeader} size={30} disableBorder/>
                                    <Text style={{color:colors.text, fontSize:12, fontFamily:fonts.heading}}>{study.CoLeader?.firstName} {study.CoLeader?.lastName}</Text>
                                </View>
                            ) : (
                                <></>
                            )},
                            {accessor: "students", value: "Students", renderer: (study) => (
                                <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                                    {study.Students?.slice(0, 3).map((student, i) => (
                                        <View key={student.Person?.id} style={{marginLeft:i > 0 ? -15 : 0}}>
                                            <Avatar profile={student.Person} size={30} disableBorder/>
                                        </View>
                                    ))}

                                    {study.Students?.length > 3 && (
                                        <View style={{marginLeft:-15}}>
                                            <Tooltip title={study.Students?.slice(3).map(student => student.Person?.firstName + " " + student.Person?.lastName).join(", ")}>
                                                <View style={{backgroundColor:colors.primary, paddingVertical:2, paddingHorizontal:4, borderRadius:30, height:30, width:30, alignItems:"center", justifyContent:"center"}}>
                                                    <Text style={{color:colors.onPrimary, fontSize:12, fontFamily:fonts.heading}}>+{study.Students?.length - 3}</Text>
                                                </View>
                                            </Tooltip>
                                        </View>
                                    )}
                                </View>
                            )},
                            {accessor: "closedAt", value: "Closed", sortable: true, renderer: (study) => study.closedAt ? new Date(study.closedAt).toLocaleDateString() : "-"},
                        ]}
                    /></View>
                    : (<View style={styles.listContainerMobile}>
                        {scopeToggle}
                    <TableFilters tableName="Bible Studies" filters={filtersWithComponents} onFiltersChange={setFilters} onClearFilters={() => setFilters(filters.map(f => ({...f, value: null})))} onSearchChange={setSearchText}/>
                        <CardGrid
                            keyExtractor={(item) => item.id}
                            estimatedItemSize={310}
                            columns={{xs: 2, md: 3, xl: 4}}
                            gap={12}
                            padding={{paddingTop: 10}}
                            data={isLoading ? [] : flatStudies}
                            renderItem={renderItem}
                            onEndReachedThreshold={0.1}
                            onEndReached={() => {
                                if(!isLoading && !isFetchingNextPage && hasNextPage) fetchNextPage()
                            }}
                            refreshing={isFetching && !isLoading && !isFetchingNextPage}
                            onRefresh={() => {
                                queryClient.invalidateQueries({queryKey: QueryKeys.bibleStudies.all()});
                            }}
                            ListFooterComponent={!isLoading
                                ? <View style={{paddingBottom:80}}>{
                                    flatStudies?.length == 0 ? <EmptyListPlaceholder icon="book-outline" spacing={10} message={effectiveScope === 'own' ? "You aren't teaching any Bible Studies yet!" : "No Bible Studies yet!"}/>
                                    : isFetchingNextPage ? <ActivityIndicator size="small" color={colors.primary} /> : <View></View>
                                }
                                </View>
                                : <View style={{alignContent:'center', justifyContent:'center'}}>
                                    <ActivityIndicator size="large" color={colors.primary} />
                                </View>
                            }
                        />
                    </View>)
                }
                                </View>
                            );
                        default:
                            return null;
                    }
                }}
            />
        </View>
    )
}


const styles = StyleSheet.create(({colors, fonts}) => ({
    container:{
        flex:1,
    },
    loadingContainer:{
        flex:1,
        alignItems:'center',
        justifyContent:'center',
    },
    listContainer:{
        flex:1,
        width:"100%",
    },
    listContainerMobile:{
        flex:1,
        padding:10,
    },
    tableContainer: {
        flex:1,
        paddingTop:10
    },
    scopeRow: {
        flexDirection:"row",
        gap:8,
        paddingHorizontal:12,
        paddingBottom:8,
    },
    scopePill: (selected) => ({
        paddingVertical:6,
        paddingHorizontal:14,
        borderRadius:20,
        backgroundColor: selected ? colors.groupsContainer : colors.surface,
        borderWidth:1,
        borderColor: selected ? colors.groups : colors.border,
    }),
    scopePillText: (selected) => ({
        fontSize:12,
        fontFamily: selected ? fonts.bold : fonts.body,
        color: selected ? colors.groups : colors.textSecondary,
    }),
    pill:(statusColor, statusTextColor) => ({
        paddingVertical:3,
        paddingHorizontal:10,
        margin:3,
        borderWidth:1,
        borderColor: colors[statusTextColor],
        backgroundColor:colors[statusColor],
        borderRadius:8,
        width:"fit-content",
    }),
    pillText:(color) => ({
        fontSize:13,
        color:colors[color],
        fontFamily:fonts.bold,
        textAlign:'center'
    }),
    teacherFilterContainer: {
        gap: 8,
    }
}))
